import { ChangeDetectorRef, Component, HostListener, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserTableService } from 'src/app/core/database/user-table.service';
import { ReportService } from './reports.service';
import { IReportView } from './IReportView';
import { DATE_FORMAT, ERROR_GETTING_DATA } from 'src/app/app.constants';
import { FavouriteTableService } from 'src/app/core/database/favourite-table.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserService } from 'src/app/core/services/user.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';

@Component({
  selector: 'reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  constructor(
    private readonly reportService: ReportService,
    private readonly userTableService: UserTableService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly favouriteTableService: FavouriteTableService,
    private readonly snackBar: MatSnackBar,
    private readonly userService: UserService,
    private readonly matDialog: MatDialog
  ) {}

  @Input() subReports = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setMaxColumns(window.innerWidth);
  }

  dateFormat = DATE_FORMAT;
  errorGettingData = ERROR_GETTING_DATA;

  reports: IReportView[] = [];
  tabs: string[];
  activeTab: string;
  maxNumOfColumns = 2;
  staffId: number;
  loading = true;
  error = false;

  handlingRequest = false;

  searchString = '';

  readonly filterIcon = 'filter_list';
  readonly clearFilterIcon = 'close';

  ngOnInit(): void {
    this.setMaxColumns(window.innerWidth);

    this.userTableService.get(1).then((staffResponse) => {
      if (!staffResponse || staffResponse === undefined) {
        this.router.navigate(['']);
      }
      this.staffId = staffResponse.staffId;
      this.getReports();
    });
  }

  setMaxColumns(screenWidth) {
    if (screenWidth < 1080) {
      this.maxNumOfColumns = 1;
    } else if (screenWidth < 1430) {
      this.maxNumOfColumns = 2;
    } else if (screenWidth < 1820) {
      this.maxNumOfColumns = 3;
    } else {
      this.maxNumOfColumns = 4;
    }

    this.cdr.detectChanges();
  }

  searchReportChange($event: string) {
    this.searchString = $event.trim().toLowerCase();
  }

  getReports() {
    this.loading = true;
    this.error = false;

    const endPoint = this.subReports
      ? this.reportService.getSubReportList(this.staffId)
      : this.reportService.getFullReportList(this.staffId);

    const subscription = endPoint.subscribe({
      next: (data: { reports: IReportView[]; favAndFiltersSuccess: boolean }) => {
        this.reports = data.reports;
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
        this.error = true;
        this.cdr.detectChanges();
      },
      complete: () => {
        this.tabs = this.reports
          .map((r) => r.tab)
          .filter((value, index, self) => self.indexOf(value) === index)
          .sort();
        this.activeTab = this.tabs[0];

        subscription.unsubscribe();
        this.loading = false;
        this.filtersToLocalDb();
        this.cdr.detectChanges();
      },
    });
  }

  haveFavourites(): boolean {
    return this.reports.some((r) => r.favouriteId !== null);
  }

  haveAnyReports(): boolean {
    return this.getFilteredReports(false).length > 0;
  }

  getMinColCount(favourite: boolean) {
    const itemCount = this.getFilteredReports(favourite).length;
    if (itemCount === 0) {
      return 0;
    }
    return Math.min(Math.ceil(itemCount / Math.ceil(itemCount / this.maxNumOfColumns)), this.maxNumOfColumns);
  }

  getColItems(favourite: boolean, column: number) {
    const allItems = this.getFilteredReports(favourite);
    const colSize = Math.ceil(allItems.length / this.maxNumOfColumns);
    return column !== this.maxNumOfColumns - 1
      ? allItems.slice(column * colSize, column * colSize + colSize)
      : allItems.slice(column * colSize);
  }

  private getFilteredReports(favouriteSection: boolean): IReportView[] {
    const searchActive = this.searchString !== '';
    const activeTab = this.activeTab ?? '';

    return this.reports.filter((report) => {
      const isFavourite = report.favouriteId !== null;

      if (favouriteSection) {
        if (!isFavourite) {
          return false;
        }
      } else if (isFavourite && !searchActive) {
        return false;
      }

      if (searchActive) {
        return report.name.toLowerCase().includes(this.searchString);
      }

      if (favouriteSection) {
        return true;
      }

      return activeTab === '' || report.tab === activeTab;
    });
  }

  changeTab(tab: string) {
    this.activeTab = tab;
  }

  filtersToLocalDb() {
    this.favouriteTableService.clear();

    this.reports.forEach((report) => {
      if (report.filters.length > 0) {
        report.filters.forEach((filter) => {
          this.favouriteTableService.add(
            {
              type: 'Filter',
              id: filter.id,
              title: filter.name,
              link: `${report.link}/${filter.id}`,
              tags: '',
              filter: filter.filterString,
              name: filter.name,
              reportId: report.id,
              createdDate: filter.createdDate,
            },
            `Filter:${filter.id}`
          );
        });
      }
    });
  }

  handleToggleFavourite(type: string, favId: number, reportId) {
    if (this.handlingRequest) {
      return;
    }

    if (favId !== null) {
      this.removeFavourite(type, reportId);
    } else {
      this.storeFavourite(reportId);
    }
  }

  handleRemoveFilter(filterId: number) {
    if (this.handlingRequest) {
      return;
    }

    const confirmDialogConfig = new MatDialogConfig();
    confirmDialogConfig.data = {
      message: `Are you sure you want to delete this saved filter?`,
      title: 'Are you sure?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      type: 'warn',
    };

    confirmDialogConfig.restoreFocus = false;
    confirmDialogConfig.disableClose = true;

    this.matDialog
      .open(ConfirmDialogComponent, confirmDialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.removeFavourite('Filter', filterId);
        }
      });
  }

  removeFavourite(type: string, id: number) {
    this.handlingRequest = true;
    const data = { type: type, id: id, staffId: this.staffId };
    const subscription = this.userService.removeFavouriteReport(data).subscribe({
      next: () => {
        if (type === 'Report') {
          this.reports.find((r) => r.id === id).favouriteId = null;
        } else if (type === 'Filter') {
          this.reports.find((r) => r.filters.find((f) => f.id === id)).filters = this.reports
            .find((r) => r.filters.find((f) => f.id === id))
            .filters.filter((f) => f.id !== id);
        }
      },
      error: () => {
        this.handlingRequest = false;
        this.snackBar.open(
          type === 'Report' ? 'Failed to remove favourite report.' : 'Failed to remove saved filter.',
          'OK',
          { duration: 2000 }
        );
        subscription.unsubscribe();
        this.cdr.detectChanges();
      },
      complete: () => {
        this.handlingRequest = false;
        this.filtersToLocalDb();
        this.snackBar.open(type === 'Report' ? 'Favourite report removed.' : 'Saved filter removed.', 'OK', {
          duration: 2000,
        });
        subscription.unsubscribe();
        this.cdr.detectChanges();
      },
    });
  }

  // Only should be type of report
  storeFavourite(id: number) {
    this.handlingRequest = true;
    const data = { type: 'Report', reportId: id, staffId: this.staffId, storeFilter: { name: null, filter: null } };
    const subscription = this.userService.storeFavouriteReport(data).subscribe({
      next: (response: number) => {
        const report = this.reports.find((r) => r.id === id);
        report.favouriteId = response;
      },
      error: () => {
        this.handlingRequest = false;
        this.snackBar.open('Failed to store favourite report.', 'OK', { duration: 2000 });
        subscription.unsubscribe();
        this.cdr.detectChanges();
      },
      complete: () => {
        this.handlingRequest = false;
        this.snackBar.open('Favourite report added.', 'OK', { duration: 2000 });
        subscription.unsubscribe();
        this.cdr.detectChanges();
      },
    });
  }
}
